import React from "react";
import { motion } from "framer-motion";
import { InView, LineReveal } from "../motion/Reveal";
import { mediaUrl } from "@/lib/api";
import { useLang } from "@/lib/lang";

export default function Experts({ experts }) {
  const { t } = useLang();
  if (!experts || !(experts.photos || []).length) return null;

  const heading = experts.heading || { en: "We are", es: "Somos" };
  const highlight = experts.highlight || { en: "Experts.", es: "Expertos." };
  const sub = experts.sub || {
    en: "Trained crew, real blueprints, disciplined work. What you see is who we are.",
    es: "Equipo entrenado, planos reales, trabajo disciplinado. Lo que ves es quienes somos.",
  };
  const tag = experts.tag || { en: "/ 01.5 — Crew", es: "/ 01.5 — Equipo" };

  const p0 = experts.photos[0];
  const p1 = experts.photos[1];

  return (
    <section
      id="experts"
      data-testid="experts-section"
      className="relative py-20 md:py-40 bg-[#0A0A0A] overflow-hidden"
    >
      <div className="absolute inset-0 grid-blueprint opacity-25" />
      <div className="absolute -top-40 right-1/4 w-[600px] h-[600px] glow-blue opacity-40 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 md:px-14">
        <div className="grid grid-cols-12 gap-6 md:gap-12 items-center">
          {/* Left column: text */}
          <div className="col-span-12 md:col-span-5">
            <p className="num-tick mb-4">{t(tag)}</p>
            <h2
              className="font-display uppercase text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9]"
              data-testid="experts-title"
            >
              <LineReveal text={t(heading)} />
              <span className="block text-[#1E7FFF] txt-shadow-blue">
                <LineReveal text={t(highlight)} delay={0.2} />
              </span>
            </h2>
            <p className="mt-6 md:mt-8 text-white/70 text-base md:text-lg font-body max-w-md leading-relaxed">
              {t(sub)}
            </p>

            {/* stat row */}
            <div className="mt-8 md:mt-10 grid grid-cols-3 gap-4 max-w-md border-t border-white/10">
              <StatBox num="5+" label={{ en: "Years", es: "Años" }} />
              <StatBox num="500+" label={{ en: "Homes", es: "Casas" }} />
              <StatBox num="24h" label={{ en: "Quote", es: "Cotización" }} />
            </div>
          </div>

          {/* Right column: photos */}
          <div className="col-span-12 md:col-span-7">
            {/* Mobile: side-by-side grid; Desktop: overlapped absolute */}
            <div className="grid grid-cols-2 gap-3 md:hidden">
              <PhotoCard photo={p0} label="01" labelText={{ en: "On site", es: "En obra" }} tilt={-1.5} accent="white" objectY="22%" />
              <PhotoCard photo={p1} label="02" labelText={{ en: "Blueprints", es: "Planos" }} tilt={1.5} accent="blue" objectY="30%" />
            </div>

            <div className="hidden md:block relative min-h-[640px]">
              <motion.div
                initial={{ opacity: 0, y: 40, rotate: -3 }}
                whileInView={{ opacity: 1, y: 0, rotate: -2 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                className="absolute left-0 top-0 w-[52%] aspect-[3/4] overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] border border-white/10 z-10"
              >
                {p0 && (
                  <img
                    src={mediaUrl(p0.url)}
                    alt={t(p0.caption)}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "center 22%" }}
                    loading="lazy"
                  />
                )}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-4 left-4 num-tick text-white/90">
                  01 · {t({ en: "On site", es: "En obra" })}
                </div>
                <div className="corner-ticks" />
                <div className="absolute right-0 bottom-0 w-1/2 h-10 bg-gradient-to-l from-black/95 to-transparent pointer-events-none" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 60, rotate: 4 }}
                whileInView={{ opacity: 1, y: 0, rotate: 2 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="absolute right-0 top-24 w-[58%] aspect-[4/3] overflow-hidden shadow-[0_30px_80px_-20px_rgba(30,127,255,0.35)] border border-[#1E7FFF]/30 z-20"
              >
                {p1 && (
                  <img
                    src={mediaUrl(p1.url)}
                    alt={t(p1.caption)}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "center 30%" }}
                    loading="lazy"
                  />
                )}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-4 left-4 num-tick text-[#1E7FFF]">
                  02 · {t({ en: "Blueprints", es: "Planos" })}
                </div>
                <div className="corner-ticks" />
                <div className="absolute right-0 bottom-0 w-1/2 h-10 bg-gradient-to-l from-black/95 to-transparent pointer-events-none" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.85 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="absolute bottom-0 left-[10%] z-30 border border-[#1E7FFF] bg-black/80 backdrop-blur-md px-4 py-3"
              >
                <p className="num-tick text-[#1E7FFF]">CERTIFIED · BILINGUAL</p>
                <p className="font-display uppercase text-white text-2xl md:text-3xl leading-none mt-1">
                  Raleigh, NC
                </p>
              </motion.div>
            </div>

            {/* Mobile stamp below photos */}
            <div className="md:hidden mt-5 inline-block border border-[#1E7FFF] bg-black/80 backdrop-blur-md px-4 py-3">
              <p className="num-tick text-[#1E7FFF]">CERTIFIED · BILINGUAL</p>
              <p className="font-display uppercase text-white text-2xl leading-none mt-1">
                Raleigh, NC
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PhotoCard({ photo, label, labelText, tilt = 0, accent = "white", objectY = "center" }) {
  const { t } = useLang();
  if (!photo) return null;
  const borderCls = accent === "blue" ? "border-[#1E7FFF]/30" : "border-white/10";
  const labelCls = accent === "blue" ? "text-[#1E7FFF]" : "text-white/90";
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, rotate: tilt * 1.5 }}
      whileInView={{ opacity: 1, y: 0, rotate: tilt }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className={`relative aspect-[3/4] overflow-hidden border ${borderCls} shadow-[0_20px_50px_-15px_rgba(0,0,0,0.7)]`}
    >
      <img
        src={mediaUrl(photo.url)}
        alt={t(photo.caption)}
        className="w-full h-full object-cover"
        style={{ objectPosition: `center ${objectY}` }}
        loading="lazy"
      />
      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/80 to-transparent" />
      <div className={`absolute bottom-3 left-3 num-tick ${labelCls}`}>
        {label} · {t(labelText)}
      </div>
      <div className="corner-ticks" />
      <div className="absolute right-0 bottom-0 w-1/2 h-8 bg-gradient-to-l from-black/95 to-transparent pointer-events-none" />
    </motion.div>
  );
}

function StatBox({ num, label }) {
  const { t } = useLang();
  return (
    <InView className="border-b border-white/10 py-4">
      <div className="font-display text-[#1E7FFF] text-3xl md:text-4xl">{num}</div>
      <div className="num-tick mt-1 text-white/60">{t(label)}</div>
    </InView>
  );
}
